import { useEffect, useRef } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { notification } from "antd";
import socket from "../Sales1/socket";
import { openTab } from "../../../Slices/tabSlice";
import {
  upsertWaConversation, upsertWaMessage, patchWaMessage, fetchWaStatus, fetchWaConversations, fetchWaMessages, setWaActive, fetchWaTemplates,
} from "../../../Slices/whatsappSlice";

const COUNTS_DEBOUNCE_MS = 1500;
const WA_EVENTS = {
  messageNew: "wa:message:new",
  messageStatus: "wa:message:status",
  conversationUpdated: "wa:conversation:updated",
  templatesUpdated: "wa:templates:updated",
  subscribed: "wa:subscribed",
};

/**
 * Live WhatsApp updates for the whole app (mounted once, in WebtopGlobalListeners).
 * Joins the server's verified "whatsapp:inbox" room by sending the JWT; the
 * server ignores users without inbox access. Re-subscribes after reconnects
 * and refetches the open views so nothing is missed while offline.
 */
export default function WhatsAppSocketListener() {
  const dispatch = useDispatch();
  const store = useStore();
  const loginUser = useSelector((s) => s?.loginUserInfo?.loginUserDetails);
  const countsTimer = useRef(null);
  const subscribedOnce = useRef(false);

  useEffect(() => {
    if (!loginUser?.id) return undefined;

    const subscribe = () => {
      const token = localStorage.getItem("token");
      if (token) socket.emit("wa:subscribe", { token });
    };

    const refreshCounts = () => {
      if (!store.getState().whatsapp?.status) return; // inbox never opened — nothing to refresh
      clearTimeout(countsTimer.current);
      countsTimer.current = setTimeout(() => dispatch(fetchWaStatus()), COUNTS_DEBOUNCE_MS);
    };

    const onConnect = () => subscribe();

    const onSubscribed = () => {
      // After a reconnect, re-sync whatever is on screen.
      if (subscribedOnce.current) {
        const st = store.getState().whatsapp;
        if (st?.status) {
          dispatch(fetchWaConversations());
          if (st.activeId) dispatch(fetchWaMessages({ conversationId: st.activeId }));
          refreshCounts();
        }
      }
      subscribedOnce.current = true;
    };

    const onMessageNew = ({ message, conversation } = {}) => {
      if (conversation) dispatch(upsertWaConversation(conversation));
      if (message) dispatch(upsertWaMessage(message));
      refreshCounts();

      // Toast for inbound messages that need this user's attention and aren't on screen.
      if (message?.direction !== "inbound" || !conversation) return;
      const st = store.getState().whatsapp;
      const userId = loginUser.id;
      const relevant = conversation.assignedTo === userId || !conversation.assignedTo;
      const onScreen = st?.activeId === conversation.id && document.visibilityState === "visible";
      if (!relevant || onScreen) return;
      const who = conversation.contactName || conversation.displayNumber || "WhatsApp contact";
      notification.open({
        key: `wa-${conversation.id}`,
        message: `WhatsApp · ${who}`,
        description: (conversation.lastMessagePreview || "").slice(0, 140),
        placement: "bottomRight",
        duration: 6,
        onClick: () => {
          dispatch(setWaActive(conversation.id));
          dispatch(openTab({ path: "/whatsapp", title: "WhatsApp", deduplicate: true }));
          notification.destroy(`wa-${conversation.id}`);
        },
      });
    };

    const onMessageStatus = (message) => {
      if (message) dispatch(patchWaMessage(message));
    };

    const onConversationUpdated = (conversation) => {
      if (conversation) dispatch(upsertWaConversation(conversation));
      refreshCounts();
    };

    // A template was created/edited/approved/rejected — refresh the picker list if it's in use.
    const onTemplatesUpdated = () => {
      if (store.getState().whatsapp?.templates?.loaded) dispatch(fetchWaTemplates());
    };

    socket.on("connect", onConnect);
    socket.on(WA_EVENTS.subscribed, onSubscribed);
    socket.on(WA_EVENTS.messageNew, onMessageNew);
    socket.on(WA_EVENTS.messageStatus, onMessageStatus);
    socket.on(WA_EVENTS.conversationUpdated, onConversationUpdated);
    socket.on(WA_EVENTS.templatesUpdated, onTemplatesUpdated);
    if (socket.connected) subscribe();

    return () => {
      clearTimeout(countsTimer.current);
      socket.off("connect", onConnect);
      socket.off(WA_EVENTS.subscribed, onSubscribed);
      socket.off(WA_EVENTS.messageNew, onMessageNew);
      socket.off(WA_EVENTS.messageStatus, onMessageStatus);
      socket.off(WA_EVENTS.conversationUpdated, onConversationUpdated);
      socket.off(WA_EVENTS.templatesUpdated, onTemplatesUpdated);
      socket.emit("wa:unsubscribe");
    };
  }, [dispatch, store, loginUser?.id]);

  return null;
}
